import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import B2bChip from './B2bChip';
import FulfillmentProductThumb from './FulfillmentProductThumb';
import {
  LABELING_STEPS,
  cargoPlaceProgress,
  cargoPlacesOnPallet,
  labelingOrderStateInfo,
  pickingStockText as stockText,
  scanProductBlocked,
} from '../../utils/fulfillmentApi.mjs';

export function LabelNumber({ order }) {
  if (!order) return null;
  const parts = order.label_number_parts;
  if (parts && (parts.a || parts.b)) {
    return (
      <Box sx={{ textAlign: 'center', lineHeight: 1 }}>
        {parts.a && (
          <Typography component="span" variant="h5" color="text.secondary" sx={{ mr: 1, fontWeight: 600 }}>
            {parts.a}
          </Typography>
        )}
        {parts.b && (
          <Typography component="span" variant="h2" sx={{ fontWeight: 800, letterSpacing: 2 }}>
            {parts.b}
          </Typography>
        )}
      </Box>
    );
  }
  if (!order.label_number_display) return null;
  return (
    <Typography variant="h3" align="center" sx={{ fontWeight: 800, wordBreak: 'break-all' }}>
      {order.label_number_display}
    </Typography>
  );
}

export function LabelingStateChip({ state, mode = null, size = 'small' }) {
  const info = labelingOrderStateInfo(state, mode);
  return <Chip size={size} color={info.color} label={info.label} />;
}

function productIdText(current, marketplace) {
  if (marketplace === 'wb' && current.nm_id) return `nmID ${current.nm_id}`;
  if (marketplace === 'ozon' && current.sku) return `SKU ${current.sku}`;
  return null;
}

/**
 * Карточка текущего шага проклейки (общая для склада отгрузки и сборки в режиме during_picking).
 * current — OrderLabelingDTO (normalizeLabelingOrder) или карточка товара (normalizeScanProduct, `is_product`).
 * onFlush не передан — кнопки «На палету» нет. embedded — без рамки (внутри Dialog).
 */
export default function LabelingStepCard({
  current,
  step,
  marketplace = 'wb',
  labelingMode = null,
  busy = false,
  readOnly = false,
  printError = null,
  onConfirm,
  onReprint,
  onCancel,
  onFlush = null,
  embedded = false,
}) {
  if (!current) return null;
  const isProduct = Boolean(current.is_product);
  const isLocalWarehouse = marketplace === 'ozon' || marketplace === 'yandex';
  const place = isProduct ? null : cargoPlaceProgress(current);
  const placesOnPallet = place ? cargoPlacesOnPallet(current) : null;
  const productBlocked = isProduct && scanProductBlocked(current);
  const idText = isProduct ? productIdText(current, marketplace) : null;
  const stock = isProduct ? stockText(current.stock) : null;

  return (
    <Paper
      variant="outlined"
      sx={embedded ? { p: 0, border: 0 } : {
        p: 2,
        borderWidth: 2,
        borderColor: step === LABELING_STEPS.IN_HANDS ? 'success.main' : 'primary.main',
      }}
    >
      <Stack spacing={1.5}>
        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0 }}>
          <FulfillmentProductThumb task={current} marketplace={marketplace} size={72} />
          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="titleMedium" sx={{ wordBreak: 'break-word' }}>
              {current.article || 'Товар'}
            </Typography>
            {current.name && (
              <Typography variant="bodySmall" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                {current.name}
              </Typography>
            )}
            <Typography variant="bodySmall" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
              {isProduct
                ? [current.barcode ? `ШК ${current.barcode}` : null, idText].filter(Boolean).join(' · ') || '—'
                : (
                  <>
                    {marketplace === 'ozon' ? 'Отправление' : 'Заказ'} {current.external_id || '—'}
                    {current.barcode ? ` · ШК ${current.barcode}` : ''}
                  </>
                )}
            </Typography>
            <B2bChip show={!isLocalWarehouse && current.is_b2b} sx={{ mt: 0.5 }} />
          </Box>
          {isProduct ? (
            <Chip size="small" color="primary" variant="outlined" label={`${current.picked} из ${current.total}`} />
          ) : (
            <LabelingStateChip
              mode={labelingMode}
              state={step === LABELING_STEPS.IN_HANDS ? 'in_hands' : current.labeling_state}
            />
          )}
        </Stack>

        {isProduct && (
          <Stack spacing={0.5} alignItems="center">
            <Typography variant="headlineSmall" sx={{ fontWeight: 700 }}>
              {current.pending > 0 ? `Осталось собрать ${current.pending} из ${current.total}` : `Все ${current.total} уже собраны`}
            </Typography>
            {stock && (
              <Typography variant="bodySmall" color={productBlocked ? 'error.main' : 'text.secondary'}>
                {stock}
              </Typography>
            )}
          </Stack>
        )}
        {productBlocked && (
          <Alert severity="error">Доступный остаток МойСклад равен нулю — подбор заблокирован.</Alert>
        )}

        {!isProduct && (current.label_error ? (
          <Alert severity="warning">
            Стикер не получен: {current.label_error.message || 'ошибка маркетплейса'}.
            Нажми «Печать этикетки» — попробуем ещё раз.
          </Alert>
        ) : (
          <LabelNumber order={current} />
        ))}

        {place && (
          <Stack spacing={0.75} alignItems="center">
            <Typography variant="titleMedium">
              {place.done ? `Все ${place.total} мест проверены` : `Место ${place.current} из ${place.total}`}
            </Typography>
            {placesOnPallet && placesOnPallet.onPallet > 0 && (
              <Typography variant="bodySmall" color={placesOnPallet.done ? 'success.main' : 'text.secondary'}>
                На палете {placesOnPallet.onPallet} из {placesOnPallet.total}
              </Typography>
            )}
            {current.cargo_places.length > 0 && (
              <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap" justifyContent="center">
                {current.cargo_places.map((cargo, index) => (
                  <Chip
                    key={cargo.code || cargo.index || index}
                    size="small"
                    label={cargo.code || `${current.external_id}-${index + 1}`}
                    color={cargo.verified
                      ? 'success'
                      : (cargo.code && cargo.code === current.cargo_place_current?.code ? 'warning' : 'default')}
                  />
                ))}
              </Stack>
            )}
          </Stack>
        )}

        {current.composition_alert && (
          <Alert severity="warning">
            Состав отправления изменился — проверь товары. Печать только кнопкой
            {isProduct ? ' «Подтвердить и напечатать»' : ' «Печать этикетки»'}.
          </Alert>
        )}
        {printError && (
          <Alert severity="error">
            Не удалось отправить на печать: {printError.message || 'ошибка печати'}
          </Alert>
        )}

        {!readOnly && step === LABELING_STEPS.CONFIRM && (
          <>
            <Button
              fullWidth
              variant="contained"
              onClick={onConfirm}
              disabled={busy || productBlocked || (isProduct && current.pending < 1)}
              sx={{ minHeight: 56 }}
            >
              {busy
                ? <CircularProgress size={22} color="inherit" />
                : (isProduct ? 'Подтвердить и напечатать' : 'Печать этикетки')}
            </Button>
            <Button fullWidth color="inherit" onClick={onCancel} disabled={busy} sx={{ minHeight: 48 }}>
              Отменить
            </Button>
          </>
        )}
        {!readOnly && step === LABELING_STEPS.VERIFY && (
          <Button
            fullWidth
            variant="outlined"
            onClick={onReprint}
            disabled={busy}
            sx={{ minHeight: 56 }}
          >
            {busy ? <CircularProgress size={22} /> : 'Повторить печать'}
          </Button>
        )}
        {!readOnly && step === LABELING_STEPS.IN_HANDS && onFlush && (
          <Button
            fullWidth
            variant="contained"
            color="success"
            onClick={onFlush}
            disabled={busy}
            sx={{ minHeight: 56 }}
          >
            {busy ? <CircularProgress size={22} color="inherit" /> : 'На палету / Завершить'}
          </Button>
        )}
      </Stack>
    </Paper>
  );
}
